// Supabase analytics provider — integrates with the existing multi-provider
// system in analytics.ts. Collects events into a buffer that flushes to the
// POST /api/analytics/events endpoint.

import type { EventEnvelope, EventContext } from "@cieda/types";
import { AnalyticsBuffer } from "@/lib/analytics-buffer";
import { getVisitorId, getDeviceInfo } from "@/lib/fingerprint";
import {
  getOrCreateSession,
  recordActivity,
  getUtmFromUrl,
} from "@/lib/session-manager";

let buffer: AnalyticsBuffer | null = null;

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function buildContext(): Omit<EventContext, "ip_hash"> {
  const utm = getUtmFromUrl();
  const deviceInfo = getDeviceInfo();

  return {
    page_url: window.location.pathname + window.location.search,
    page_title: document.title,
    referrer: document.referrer || null,
    utm: {
      source: utm.utm_source,
      medium: utm.utm_medium,
      campaign: utm.utm_campaign,
      content: utm.utm_content,
      term: utm.utm_term,
    },
    device: {
      type: getDeviceType(),
      os: String(deviceInfo.os ?? ""),
      browser: String(deviceInfo.browser ?? ""),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
    },
    locale: "fr",
  };
}

/**
 * Creates a provider function compatible with registerAnalyticsProvider().
 * Usage:
 *   registerAnalyticsProvider(
 *     createSupabaseAnalyticsProvider(import.meta.env.VITE_API_URL + "/api/analytics/events")
 *   );
 */
export function createSupabaseAnalyticsProvider(
  endpoint: string
): (name: string, properties?: Record<string, unknown>) => void {
  buffer = new AnalyticsBuffer(endpoint);

  // Register visitor with the backend on first load
  (async () => {
    try {
      const visitorId = await getVisitorId();
      const deviceInfo = getDeviceInfo();
      const apiBase = endpoint.replace(/\/api\/analytics\/events$/, "");
      await fetch(`${apiBase}/api/analytics/visitors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fingerprint_hash: visitorId,
          device_info: deviceInfo,
        }),
      });
    } catch {
      // Best-effort — visitor will be registered on next event ingestion
    }
  })();

  // Set up page unload handler
  if (typeof window !== "undefined") {
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        buffer?.flushOnUnload();
      }
    });
  }

  return (name: string, properties?: Record<string, unknown>) => {
    // Fire-and-forget — async internally but sync interface
    (async () => {
      try {
        const visitorId = await getVisitorId();
        const session = getOrCreateSession(getUtmFromUrl());

        const envelope: EventEnvelope = {
          event_id: crypto.randomUUID(),
          event_type: name,
          visitor_id: visitorId,
          customer_id: null, // set server-side if visitor is known
          session_id: session.session_id,
          timestamp: new Date().toISOString(),
          context: buildContext() as EventContext,
          payload: properties ?? {},
        };

        buffer?.push(envelope);
        recordActivity(name);
      } catch (error) {
        console.warn("[supabase-provider] Failed to enqueue event:", error);
      }
    })();
  };
}

/**
 * Returns the buffer instance for manual flush control (e.g., on logout).
 */
export function getAnalyticsBuffer(): AnalyticsBuffer | null {
  return buffer;
}
